import { UseQueryResult, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useAuthentication } from '../../Idp/useAuthentication';
import { useCoreApi } from '../api';
import { ICause } from '../causes';
import { Session, User } from './users.types';

interface LimitedUser
  extends Pick<
    User,
    'email' | 'first_name' | 'last_name' | 'uuid' | 'referral_code' | 'referral_url'
  > {}

export interface UserEditInfoResponse {
  user: User;
}

type UserRequest = {
  email: string;
  password: string;
};

interface UserCreateAccount extends UserRequest {
  first_name?: string;
  last_name?: string;
  password_confirmation?: string;
  signup_referral_code?: string;
}
interface UserChangePassword extends Omit<UserRequest, 'email'> {
  password: string;
  password_confirmation: string;
  current_password: string;
}
interface UserResetPassword extends Omit<UserChangePassword, 'current_password'> {
  reset_password_token: string;
}
export type UserSignInRequest = {
  user: UserRequest;
};

export interface UserCreateAccountRequest {
  user: UserCreateAccount;
}
export interface UserChangePasswordRequest {
  user: UserChangePassword;
}
export interface UserResetPasswordRequest {
  user: UserResetPassword;
}

export function useUserData<T = User>(select?: (data: User) => T) {
  const { isAuthenticated } = useAuthentication();

  const CoreApi = useCoreApi();
  const getUserAccounts = async () => {
    const { data } = await CoreApi.get<{ user: User }>('/users/me');

    return data.user;
  };

  return useQuery({
    queryKey: ['user'],
    queryFn: getUserAccounts,
    select,
    enabled: isAuthenticated,
  });
}

type CreateAccountParams = {
  successCallback?: (user: User) => void;
  signIn?: boolean;
};
export function useCreateNewUserAccount({
  successCallback,
  signIn = true,
}: CreateAccountParams = {}) {
  const { logIn } = useAuthentication();
  const CoreApi = useCoreApi();

  const createUserAccount = async (editInfoObject: UserCreateAccountRequest): Promise<User> => {
    const { data } = await CoreApi.post('/users', { ...editInfoObject });
    return data.user;
  };

  return useMutation({
    mutationFn: createUserAccount,
    onSuccess(data) {
      if (signIn) {
        return logIn(data, successCallback);
      }
    },
  });
}

export function useLogInUser(successCallback?: (user: User) => void) {
  const { logIn } = useAuthentication();
  const CoreApi = useCoreApi();

  const logInUser = async (userRequest: UserSignInRequest) => {
    const { data } = await CoreApi.post<{ user: User }>('users/sign_in', userRequest);
    return data.user;
  };

  return useMutation({
    mutationFn: logInUser,
    onSuccess(data) {
      logIn(data, successCallback);
    },
  });
}

export function useChangeUserPassword() {
  const CoreApi = useCoreApi();
  const queryClient = useQueryClient();
  const changeUserPassword = async (editInfoObject: UserChangePasswordRequest): Promise<User> => {
    const { data } = await CoreApi.put('users', editInfoObject);
    return data.user;
  };

  return useMutation({
    mutationFn: changeUserPassword,
    onSuccess() {
      queryClient.invalidateQueries({
        queryKey: ['users'],
      });
    },
  });
}

export function useResetPassword() {
  const CoreApi = useCoreApi();
  const resetPassword = async (resetPasswordObject: { user: { email: string } }) => {
    await CoreApi.post('users/password', resetPasswordObject);
  };

  return useMutation({
    mutationFn: resetPassword,
  });
}

export function useResetPasswordWithToken() {
  const CoreApi = useCoreApi();
  const resetPassword = async (resetPasswordObject: UserResetPasswordRequest) => {
    await CoreApi.put('users/password', resetPasswordObject);
  };

  return useMutation({
    mutationFn: resetPassword,
  });
}

//  Selectors
export function useUserSession() {
  return useUserData((user: User) => user.session);
}
export function useUserCause(): UseQueryResult<ICause | undefined> {
  return useUserData((user: User) => user.cause);
}
export function useGetUserCarteraMemberId() {
  return useUserData((user: User) => user.cartera_member_id);
}

export const useCreateShortSession = () => {
  const CoreApi = useCoreApi();

  const createShortSession = async ({ setLoader }: { setLoader?: () => void } = {}) => {
    const api = CoreApi;

    try {
      const { data } = await api.post('/short/sessions');
      return data;
    } catch (error) {
      setLoader?.();
      return false;
    }
  };
  return createShortSession;
};

/** Given a session token, get the session from the API */
export const useGetShortSession = () => {
  const CoreApi = useCoreApi();

  const getShortSession = async ({ hash }: { hash: string }) => {
    const { data } = await CoreApi.get<{ session: Session }>(`/short/sessions/${hash}`);
    return data.session;
  };

  return useMutation({
    mutationFn: getShortSession,
  });
};

export const useFindOrCreateUserFromEmail = () => {
  const CoreApi = useCoreApi();

  const handleFindOrCreate = async (email?: string): Promise<LimitedUser> => {
    const { data } = await CoreApi.put('/user/find-or-create', {
      user: { email },
    });

    return data.user;
  };

  return useMutation({
    mutationFn: handleFindOrCreate,
  });
};
